import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import { FunctionComponent, useMemo, useState } from 'react';
import { useEmailSenderData } from './hooks/useEmailSenderData';
import { validateSenderEmail } from './utils/validateSenderEmail';
import styles from './SenderFormModal.module.scss';

interface SenderFormModalProps {
    /** First-time setup vs editing an existing address. Drives the title and
     *  the prefill behaviour. */
    mode: 'setup' | 'edit';
    /** Empty string for setup, the current address for edit. */
    initialEmail: string;
    /** User closed the modal without saving (Cancel button or X). */
    onCancel: () => void;
    /** Save succeeded — the slice now has fresh data (pending most likely).
     *  Parent dismisses the modal in response. */
    onSubmitted: () => void;
}

const TITLE_BY_MODE: Record<SenderFormModalProps['mode'], string> = {
    setup: 'Setup Custom Sender',
    edit: 'Edit Custom Sender'
};

/**
 * Modal used for both first-time setup and edit flows. The state component
 * underneath (Unconfigured / Pending / Verified / Failed) stays visible
 * behind the dim — this is the pattern the designer mocks (mockups 2 & 5).
 *
 * Validation:
 *   - Local regex check, surfaced only after the user blurs the input or
 *     hits Save, so we don't shout "invalid" while they're still typing.
 *   - Backend errors take precedence over local format errors (e.g. the BE
 *     might reject a duplicate / blocked domain that our regex accepts).
 */
const SenderFormModal: FunctionComponent<SenderFormModalProps> = ({ mode, initialEmail, onCancel, onSubmitted }) => {
    const { saving, errors, updateSender } = useEmailSenderData();
    const [email, setEmail] = useState<string>(initialEmail);
    const [touched, setTouched] = useState<boolean>(false);

    const trimmed = email.trim();
    const isValidLocal = useMemo(() => validateSenderEmail(trimmed), [trimmed]);

    const localError = touched && trimmed.length > 0 && !isValidLocal ? 'Please enter a valid email address.' : null;
    const backendError = errors.length > 0 ? errors.join(' ') : null;
    const inputError = backendError ?? localError;

    const canSubmit = !saving && trimmed.length > 0 && isValidLocal;

    const handleSave = async () => {
        setTouched(true);
        if (!isValidLocal) return;
        const result = await updateSender({ enabled: true, senderEmail: trimmed });
        if (result) onSubmitted();
    };

    return (
        <GenericModal
            className={styles.modal}
            onClose={saving ? undefined : onCancel}
            footer={
                <div className={styles.footer}>
                    <Button
                        size="large"
                        className={styles.footerButton}
                        label="Cancel"
                        variant="black"
                        onClick={onCancel}
                        disabled={saving}
                    />
                    <Button
                        size="large"
                        className={styles.footerButton}
                        label="Save & Verify"
                        variant="green"
                        onClick={handleSave}
                        isLoading={saving}
                        disabled={!canSubmit}
                    />
                </div>
            }
        >
            <div className={styles.body}>
                <h3 className={styles.title}>{TITLE_BY_MODE[mode]}</h3>
                <div className={styles.bodyContent}>
                    <Input
                        label="Sender Email"
                        value={email}
                        onChange={event => setEmail(event.target.value)}
                        onBlur={() => setTouched(true)}
                        error={inputError}
                        autoFocus
                        required
                        autoComplete="off"
                    />
                </div>
            </div>
        </GenericModal>
    );
};

export default SenderFormModal;
